import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { Clock, Users, Heart, AlertCircle, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { usePatientStore } from '../stores/patient';
import { useQueue } from '../hooks/useQueue';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { KoyoBranding } from '../components/KoyoBranding';
import { Button } from '../components/ui/Button';
import { ConnectionStatus, ConnectionIndicator } from '../components/ConnectionStatus';

export function QueueWaiting() {
  const navigate = useNavigate();
  const { patient, queueEntry, clear } = usePatientStore();
  const { isOnline } = useOnlineStatus();

  // Handle when patient is called
  const handleCalled = useCallback(() => {
    navigate('/consultation');
  }, [navigate]);

  // Handle queue errors
  const handleError = useCallback((error: Error) => {
    console.error('Queue error:', error);
    toast.error('Connection issue. Your place in queue is saved.');
  }, []);

  const {
    position,
    estimatedWaitMinutes,
    status,
    isConnected,
    isReconnecting,
    connectionError,
    leaveQueue,
  } = useQueue({
    onCalled: handleCalled,
    onError: handleError,
    pollingInterval: 5000,
    useWebSocket: true,
  });

  // Redirect to registration if no queue entry
  useEffect(() => {
    if (!queueEntry || !patient) {
      navigate('/');
    }
  }, [queueEntry, patient, navigate]);

  // Navigate to consultation when called
  useEffect(() => {
    if (status === 'called' || status === 'in_consultation') {
      navigate('/consultation');
    }
  }, [status, navigate]);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const handleLeaveQueue = async () => {
    setIsLeaving(true);
    try {
      await leaveQueue();
      clear();
      navigate('/');
    } catch {
      // Error already handled in hook
    } finally {
      setIsLeaving(false);
      setShowCancelModal(false);
    }
  };

  const displayPosition = position > 0 ? position : queueEntry?.position ?? 0;
  const displayWaitTime = estimatedWaitMinutes > 0 ? estimatedWaitMinutes : queueEntry?.estimatedWaitMinutes ?? 0;

  return (
    <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-6">
      {/* Connection Status Banner */}
      <ConnectionStatus
        isConnected={isConnected}
        isReconnecting={isReconnecting}
        isOnline={isOnline}
      />

      <div className="w-full max-w-2xl flex-1 flex flex-col justify-center">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="bg-emerald-600 p-3 rounded-2xl">
              <Heart className="size-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl text-white mb-2">You're in the Queue</h1>
          <p className="text-lg text-slate-400">Please wait for your turn</p>

          {/* Connection status indicator */}
          <div className="flex items-center justify-center mt-3">
            <ConnectionIndicator
              isConnected={isConnected}
              isReconnecting={isReconnecting}
              isOnline={isOnline}
            />
          </div>
        </div>

        {/* Connection Error Alert */}
        {connectionError && !isReconnecting && (
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-2xl flex items-center gap-3">
            <AlertCircle className="size-6 text-yellow-400 flex-shrink-0" />
            <p className="text-yellow-300 text-sm">
              Having trouble connecting. Your place in queue is saved.
            </p>
          </div>
        )}

        {/* Queue Card */}
        <div className="bg-[#1e293b] rounded-3xl p-10 shadow-2xl border border-slate-700 mb-6">
          {displayPosition === 0 || status === 'called' ? (
            <div className="text-center">
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-32 h-32 bg-emerald-600 rounded-full mb-4 animate-pulse">
                  <span className="text-6xl">&#10003;</span>
                </div>
              </div>
              <h2 className="text-3xl text-white mb-2">It's Your Turn!</h2>
              <p className="text-xl text-slate-400">Connecting you to a doctor...</p>
              <Loader2 className="size-8 text-emerald-500 animate-spin mx-auto mt-4" />
            </div>
          ) : (
            <>
              {/* Queue Position */}
              <div className="text-center mb-10">
                <p className="text-lg text-slate-400 mb-4">Your position in queue</p>
                <div className="relative inline-flex items-center justify-center">
                  {/* Outer pulsing ring */}
                  <div className="absolute w-44 h-44 bg-emerald-500/20 rounded-full animate-pulse" />
                  {/* Main circle */}
                  <div className="relative inline-flex items-center justify-center w-40 h-40 bg-emerald-600 rounded-full shadow-2xl">
                    <span className="text-8xl text-white font-bold">{displayPosition}</span>
                  </div>
                </div>
              </div>

              {/* Wait Time Info */}
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-2">
                    <Clock className="size-6 text-blue-400" />
                    <span className="text-slate-400">Estimated Wait</span>
                  </div>
                  <p className="text-3xl text-white">{displayWaitTime} min</p>
                </div>
                <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="size-6 text-emerald-400" />
                    <span className="text-slate-400">People Ahead</span>
                  </div>
                  <p className="text-3xl text-white">{displayPosition > 0 ? displayPosition - 1 : 0}</p>
                </div>
              </div>

              {/* Patient Info */}
              {patient && (
                <div className="bg-slate-800/50 rounded-2xl p-4 mb-6 border border-slate-700">
                  <p className="text-slate-400 text-sm mb-1">Registered as</p>
                  <p className="text-white text-lg font-medium">
                    {patient.firstName} {patient.lastName}
                  </p>
                </div>
              )}

              {/* Reassuring Message */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-6">
                <p className="text-lg text-blue-300 text-center">
                  Thank you for your patience. A doctor will see you soon.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Cancel / Leave Queue Button — allows staff to reset the kiosk */}
        {status === 'waiting' && (
          <Button
            onClick={() => setShowCancelModal(true)}
            variant="outline"
            className="w-full h-16 text-lg bg-slate-800 border-red-800 text-red-300 hover:bg-red-900/40 hover:text-red-200 rounded-2xl"
          >
            Cancel & Return to Registration
          </Button>
        )}
      </div>

      {/* Koyo Branding */}
      <KoyoBranding className="py-6" />

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowCancelModal(false)}
          />
          {/* Modal */}
          <div className="relative bg-[#1e293b] rounded-3xl p-8 max-w-md w-full border border-slate-600 shadow-2xl">
            <button
              onClick={() => setShowCancelModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <X className="size-6" />
            </button>

            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-full mb-4">
                <AlertCircle className="size-8 text-red-400" />
              </div>
              <h2 className="text-2xl text-white mb-2">Leave the Queue?</h2>
              <p className="text-slate-400">
                You will lose your place in the queue and need to register again.
              </p>
            </div>

            <div className="flex gap-4">
              <Button
                onClick={() => setShowCancelModal(false)}
                variant="outline"
                className="flex-1 h-14 text-lg bg-slate-800 border-slate-600 text-white hover:bg-slate-700 rounded-xl"
              >
                Stay in Queue
              </Button>
              <Button
                onClick={handleLeaveQueue}
                disabled={isLeaving}
                className="flex-1 h-14 text-lg bg-red-600 hover:bg-red-700 text-white rounded-xl disabled:opacity-50"
              >
                {isLeaving ? (
                  <Loader2 className="size-5 animate-spin mx-auto" />
                ) : (
                  'Yes, Leave'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
